// Source of truth: the cleverly brand repo (D:/Source/cleverly/brand —
// brand/tokens.json + brand/brandguide.md, Stand 24.09.2026). Values below are
// copied verbatim from tokens.json; see decision log §6/§7.
//
// Exactly three elevation levels, burgundy-tinted (wine #4F1D2C), never black.
// Every shadow token below maps onto one of these — nothing stronger than DS_SHADOW_LARGE.
const DS_SHADOW_SMALL = '0 2px 6px #4f1d2c0f, 0 1px 2px #4f1d2c0a';
const DS_SHADOW_MEDIUM = '0 8px 24px #4f1d2c14';
const DS_SHADOW_LARGE = '0 12px 28px #4f1d2c1a';

// DS `orange-text`: orange as TEXT on light grounds (5.2:1 on white). The
// brand orange itself (orange.500, 2.7:1) and orange.600 fail AA as text.
const DS_ORANGE_TEXT = '#B54E23';

// DS `neutral`: the 1.5px secondary-button border (3.5:1 on white).
const DS_NEUTRAL = '#8A8A87';

export const utility = {

    // ===== ROLE TOKENS — the shared vocabulary (decision log §9) =====
    // Shared component code (SCSS, templates, component token files) references
    // ONLY these, the PrimeNG semantic vars (--p-text-color, --p-text-muted-color,
    // --p-primary-color, --p-primary-contrast-color, --p-content-background,
    // --p-content-border-color, --p-highlight-*, --p-focus-ring-*, --p-border-radius-*),
    // the state tokens and the three shadow levels. Both utility files define
    // every role, so both themes render. Values: brand guide (tokens.json).
    textheading: '{wine.900}',            // h1–h6, card/dialog titles (O2)
    textaccent: DS_ORANGE_TEXT,           // orange as text on light: highlight words, prices, checks
    textondark: '#FFFFFF',                // text on wine/dark surfaces
    textneutral: '{sand.950}',            // neutral-darkest: secondary/link button + tag labels only
    surfacepage: '{sand.50}',             // neutral-lightest: page / main BG, cream cards
    surfacecream: '{yellow.200}',         // yellow-lighter: soft accent surfaces, info chips
    surfacesoft: '#E5F2FF',               // blue-lightest: hero/info sections
    surfaceaccent: '{blue.200}',          // blue-lighter (Knowledge Blue): accent surfaces, the accent button
    surfacepeach: '#F7EDF1',              // wine-lightest: peach cards, number badges
    surfacesubtle: '#0000000D',           // neutral-darkest-5: default tag, quiet fills
    surfacedark: '{wine.900}',            // dark sections / dark card (text: textondark)
    bordercontrol: DS_NEUTRAL,            // 1.5px control borders (secondary button)
    borderselected: '{orange.500}',       // 2px border of THE highlighted/selected card (O6)

    // ===== TEXT =====
    textdisabled: 'rgba(0, 0, 0, 0.30)',

    textcolordisabled: 'rgba(0, 0, 0, .30)',

    inputbordercolorfocus: '{wine.900}',  // DS: focused fields colour their border wine
    inputbackgrounddisabled: 'rgba(0, 0, 0, .10)',

    // ===== STATE COLORS (brand guide feedback roles, decision log O3) =====
    // No green, no signal red, no orange in product UI. success = feedback-success
    // on blue-lightest; error = wine on wine-lightest; warning = the guide's
    // `hint` (wine on yellow-lightest); info has no guide role → wine on
    // blue-lightest (the Sage pair, same as selection O8). Always icon + word.
    statesuccess: '#13406C',               // feedback-success (9.3:1 on its surface)
    statesuccessbg: '#E5F2FF',             // feedback-success-surface = blue-lightest
    stateerror: '{wine.900}',              // feedback-error #4F1D2C
    stateerrorbg: '#F7EDF1',               // feedback-error-surface = wine-lightest
    statewarning: '{wine.900}',            // feedback-hint
    statewarningbg: '#FFFAE0',             // feedback-hint-surface = yellow-lightest
    stateinfo: '{wine.900}',
    stateinfobg: '#E5F2FF',                // blue-lightest

    boxshadowlayer1: DS_SHADOW_MEDIUM,     // overlays (select, popover, menus)
 
    // Guide families: app = Inter, system-ui, sans-serif (extra entries below are
    // technical fallbacks only); heading = Poppins, Arial, sans-serif — never Inter
    // as a heading fallback (the guide forbids mixing the two in one element).
    fontfamily: '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif',
    headingfontfamily: '"Poppins", Arial, sans-serif',

    // Guide: buttons min. 48px (large 52px). Inputs follow (O4) so inline rows
    // (search bar, code input) stay aligned.
    buttonheight: '48px',
    buttonfontweight: '700',               // DS button label: 700
    buttonlinkcolor: '{sand.950}',         // guide `link`: neutral-darkest, underline on hover
    buttonlinkhovercolor: '{sand.950}',

    // Surface buttons (secondary, outlined, raised icon buttons) — one family,
    // decided 2026-10-09: white surface + resting shadow-small + `neutral-lighter`
    // hairline (app deviation from the guide's 1.5px `neutral` secondary border,
    // which read too heavy next to the icon buttons; the label + shadow identify
    // the control).
    buttonsurface: '#FFFFFF',
    buttonsurfaceborder: '{sand.200}',
    buttonsurfaceshadow: DS_SHADOW_SMALL,

    // Secondary button (O14): colours come from the role tokens in button.ts
    // (transparent, bordercontrol, textneutral); only the dark-surface variant
    // needs its own tokens because shared SCSS (.cl-on-dark) applies it.
    // `alternate` (guide): secondary on wine/dark surfaces — opt in with .cl-on-dark
    buttonsecondaryaltbackground: '#FFFFFF1A',        // white-10
    buttonsecondaryalthoverbackground: '#FFFFFF33',   // white-20
    buttonsecondaryaltbordercolor: '#FFFFFF33',       // white-20 (dark-scheme border)
    buttonsecondaryaltcolor: '#FFFFFF',

    // All filled/outlined buttons (O4): 1.5px border; states change depth, not
    // colour — hover lifts 1px + shadow-small, active resets without shadow.
    buttonborderwidth: '1.5px',
    buttonhovertransform: 'translateY(-1px)',
    buttonhovershadow: DS_SHADOW_SMALL,
    buttonactivetransform: 'translateY(0)',
    buttonactiveshadow: 'none',
    buttonraisedhovershadow: DS_SHADOW_SMALL,
    buttonraisedactiveshadow: 'none',

    // Unread / notification counters (O16, revised 2026-10-09): `orange` surface +
    // `wine` digit (the guide's text-on-orange pair, 4.75:1) — orange as accent
    // surface is allowed by the guide; its "no orange" rule covers feedback
    // messages, not counters. A 2px ring in the card colour separates the badge
    // from orange buttons/underlines it overlaps. Peach stays for non-alert
    // numbers (steps, countdowns).
    badgebackground: '{orange.500}',
    badgecolor: '{wine.900}',
    badgering: '2px',
    badgeshadow: DS_SHADOW_MEDIUM,

    // cl-tag (guide Tag, O15): default = neutral-darkest-5 / neutral-darkest, 4px;
    // pill = neutral-lightest / wine / neutral-lighter border, radius-full;
    // alternate = white-10 / white.
    tagbackground: '#0000000D',
    tagcolor: '{sand.950}',
    tagradius: '4px',
    tagfontsize: '0.875rem',
    tagfontweight: '600',
    tagpillbackground: '{sand.50}',
    tagpillcolor: '{wine.900}',
    tagpillborder: '{sand.200}',
    tagpillradius: '100px',
    tagaltbackground: '#FFFFFF1A',
    tagaltcolor: '#FFFFFF',

    buttonheightlarge: '3.25rem',

    inputheight: '48px',

    buttonpaddingvertical: '14px',
    buttonpaddinghorizontal: '20px',

    inputpaddingx: '14px',
    inputpaddingy: '13px',   // 48px fields (O4)

    headingmarginblockstart: '0',
    headingmarginblockend: '0',
    headingmargininlinestart: '0',
    headingmargininlineend: '0',

    maskbg: '#00000099',   // DS neutral-darkest-60 (modals, lightbox)

    // ===== ELEVATION + MOTION — the guide's three shadow levels =====
    shadowsm: DS_SHADOW_SMALL,             // DS shadow-small: resting cards, button hover
    shadowmd: DS_SHADOW_MEDIUM,            // DS shadow-medium: floating badges, bubbles
    shadowlg: DS_SHADOW_LARGE,             // DS shadow-large: hover of clickable cards (max)
    easeentrance: 'cubic-bezier(.2, .8, .2, 1)',
    easestandard: 'cubic-bezier(.4, 0, .2, 1)',

    transitionDuration: {
        fast: '0.1s',
        normal: '0.15s',
        slow: '0.3s'
    },

    // Border radius system (preset primitive tokens).
    // DS radius-full = 100px (pill tags, avatars, number badges, dots) — replaces
    // 9999px/50%, optically identical below 200px element height.
    borderRadius: {
        rounded: '100px'
    },

    // Responsive spacing system (values emitted at bootstrap — see tokens/spacings.ts)
    spacing: {
        huge: {
            '2': 'var(--spacing-huge-2)',
            '1': 'var(--spacing-huge-1)'
        },
        medium: {
            '5': 'var(--spacing-medium-5)',
            '4': 'var(--spacing-medium-4)',
            '3': 'var(--spacing-medium-3)',
            '2': 'var(--spacing-medium-2)', 
            '1': 'var(--spacing-medium-1)'
        },
        small: {
            '3': 'var(--spacing-small-3)', 
            '2': 'var(--spacing-small-2)', 
            '1': 'var(--spacing-small-1)'   
        },
        mainBorder: 'var(--main-border)'   
    },
};

